import { SECTION_TYPE } from "@/app/report/common/constants"
import type { CanvasItem, ChartOptions, ChartType } from "@/types/pdfBuilder"
import { buildSessionDetailsItem, createGroupId } from "@/app/report/pdf/builder/utils/paletteItems"
import { getBuilderTableSections } from "@/app/report/common/sections/customTable"
import type { ReportTemplate } from "@/types/report"
import type {
  GraphSettings,
  ScatterChartSettings,
  SessionDetailsSettings,
  StatsSettings,
  TacticMapSettings,
} from "@/app/report/common/type"

type NewItem = Omit<CanvasItem, "id">

const FULL_WIDTH = 12

// 팔레트(ChartPalette PALETTE_ITEMS)와 같은 기본 크기
const SIZE: Record<string, { w: number; h: number; minW: number; minH: number }> = {
  activityInfo: { w: FULL_WIDTH, h: 4, minW: 3, minH: 2 },
  summary: { w: FULL_WIDTH, h: 4, minW: 3, minH: 4 },
  combo: { w: FULL_WIDTH, h: 5, minW: 3, minH: 3 },
  activityGraph: { w: FULL_WIDTH, h: 5, minW: 3, minH: 3 },
  table: { w: FULL_WIDTH, h: 5, minW: 3, minH: 3 },
  sessionDetails: { w: FULL_WIDTH, h: 6, minW: 6, minH: 4 },
  scatter: { w: FULL_WIDTH / 2, h: 9, minW: 4, minH: 6 },
  // Action Map: 맵과 액션 표를 한 아이템에 나란히 — 데이터 전에도 코트(맵 열 40% 기준 약 667px)가 잘리지 않는
  // 18행으로 시작하고, 표가 오면 내용에 맞춰 자동 조정된다 (2026-09-29 개편)
  map: { w: FULL_WIDTH, h: 18, minW: 4, minH: 6 },
}

/**
 * 리포트 템플릿의 섹션 순서대로 PDF 빌더 아이템을 만든다 — 빌더에 처음 들어왔을 때(저장된 PDF 템플릿이 없을 때)와
 * 새 템플릿을 만들 때, 리포트 화면과 같은 구성으로 시작하기 위한 기본 배치 (#75).
 *
 * 각 아이템은 지표(metrics)를 비워 두어 Builder 컴포넌트가 템플릿 섹션 설정을 시드한다(sectionIndex/chartIndex 로 어느 섹션인지 가리킨다).
 * Graph 는 리포트처럼 축마다 막대/선을 섞어 그리는 Chart by Player 로, Session Details 는 팔레트와 같이
 * 세션·1분·5분·15분 네 아이템(설정 공유 그룹)으로, Scatter 는 차트마다 절반 폭으로 나란히, Tactic Map 은 맵과 액션 표를
 * 한 아이템(Action Map)으로 놓는다. 빌더에 없는 섹션(Performance Profile)은 건너뛴다.
 */
export function buildDefaultItemsFromReportTemplate(
  template: ReportTemplate,
  kind: "daily" | "trend" = "daily",
): NewItem[] {
  const items: NewItem[] = []
  let y = 0
  // 빌더 Table 이 따를 수 있는 표만 놓는다 — 옛 Stats 에서 변환된 표는 BuilderDataTable 이 첫 Custom Table 로
  // 떨어뜨리므로 그대로 놓으면 같은 표가 제목만 다르게 여러 개 생긴다 (2026-10-02 보고)
  const tableSectionIndexes = new Set(
    getBuilderTableSections(template).map((section) => section.index),
  )

  const push = (
    chartType: ChartType,
    x: number,
    title: string,
    dataSource: Partial<CanvasItem["dataSource"]>,
    advance: boolean,
    options: Partial<ChartOptions> = {},
  ) => {
    const size = SIZE[chartType]
    items.push({
      chartType,
      x,
      y,
      w: size.w,
      h: size.h,
      minW: size.minW,
      minH: size.minH,
      dataSource: { type: "activity", activityIds: [], ...dataSource },
      options: { title, ...options },
    })
    if (advance) {
      y += size.h
    }
  }

  // 트렌드는 액티비티 하나를 고르지 않으므로 Activity Info 로 시작하지 않는다 (2026-10-02 요청)
  if (kind !== "trend") {
    push("activityInfo", 0, "Activity Info", { metrics: [] }, true)
  }

  template.content?.forEach((section, sectionIndex) => {
    if (!section) {
      return
    }
    switch (section.section) {
      case SECTION_TYPE.SUMMARY:
        push("summary", 0, "Summary", { sectionIndex }, true)
        break
      case SECTION_TYPE.GRAPH:
        if (Array.isArray(section.settings)) {
          ;(section.settings as GraphSettings[]).forEach((chart, chartIndex) => {
            push("combo", 0, chart.title || "Chart", { sectionIndex, chartIndex }, true)
          })
        }
        break
      // 트렌드 템플릿의 그래프 — 액티비티별(Chart by Activity)·선수별(Chart by Player) (2026-10-02 요청)
      case SECTION_TYPE.ACTIVITY_GRAPH:
        if (Array.isArray(section.settings)) {
          ;(section.settings as GraphSettings[]).forEach((chart, chartIndex) => {
            push(
              "activityGraph",
              0,
              chart.title || "Chart by Activity",
              { sectionIndex, chartIndex },
              true,
            )
          })
        }
        break
      case SECTION_TYPE.PLAYER_GRAPH:
        if (Array.isArray(section.settings)) {
          ;(section.settings as GraphSettings[]).forEach((chart, chartIndex) => {
            push("combo", 0, chart.title || "Chart by Player", { sectionIndex, chartIndex }, true)
          })
        }
        break
      case SECTION_TYPE.SESSION_DETAILS:
        if (Array.isArray(section.settings)) {
          ;(section.settings as SessionDetailsSettings[]).forEach((_chart, chartIndex) => {
            const size = SIZE.sessionDetails
            items.push(
              buildSessionDetailsItem({
                x: 0,
                y,
                ...size,
                dataSource: { type: "activity", activityIds: [], sectionIndex, chartIndex },
              }),
            )
            y += size.h
          })
        }
        break
      case SECTION_TYPE.SCATTER_GRAPH:
        if (Array.isArray(section.settings)) {
          const charts = section.settings as ScatterChartSettings[]
          // 기본 구성은 리포트 화면처럼 두 개씩 좌우로 나란히 (2026-10-02 보고: 처음 들어올 때 가로로 나오던 것).
          // 팔레트에서 새로 추가할 때는 왼쪽에 붙는다 — 그쪽은 buildItemsFromPalette 가 x=0 으로 놓는다
          charts.forEach((_chart, chartIndex) => {
            const right = chartIndex % 2 === 1
            push(
              "scatter",
              right ? SIZE.scatter.w : 0,
              "Scatter Chart",
              { sectionIndex, chartIndex },
              right || chartIndex === charts.length - 1,
            )
          })
        }
        break
      case SECTION_TYPE.CUSTOM_TABLE:
        if (
          section.settings &&
          !Array.isArray(section.settings) &&
          tableSectionIndexes.has(sectionIndex)
        ) {
          push(
            "table",
            0,
            (section.settings as StatsSettings).title || "Table",
            { sectionIndex },
            true,
            { groupId: createGroupId("tbl") },
          )
        }
        break
      case SECTION_TYPE.TACTIC_MAP: {
        const title = (section.settings as TacticMapSettings | undefined)?.title || "Action Map"
        push("map", 0, title, { sectionIndex }, true, { showActionTable: true })
        break
      }
      default:
        break
    }
  })

  return items
}
